'use client';
import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Category,
  Location,
  People,
  Messages1,
  Cpu,
  Briefcase,
  Task,
  FolderCloud,
  ReceiptItem,
  SecurityUser,
  Chart,
  Setting2,
  SearchNormal1,
  ArrowLeft2,
  ArrowRight3,
  Logout,
  Monitor,
  Gallery,
  Hierarchy3
} from 'iconsax-react';
import { ChevronLeft, Search, LogOut, HelpCircle } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa'; // Keeping WA icon as Iconsax doesn't have it
import { useTour } from './TourWizard';
import { getSidebarBadges } from './actions';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
  group: string;
  adminOnly?: boolean;
  activeColor: string;
}

const navItems: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', href: '/dashboard', icon: Category, group: 'overview', activeColor: '#8cc63f' },
  { id: 'nav-scraper', label: 'Scraper Google Maps', href: '/scraper', icon: Location, group: 'acquisition', activeColor: '#3b82f6' },
  { id: 'nav-leads', label: 'Lead Intelligence', href: '/leads', icon: People, group: 'acquisition', activeColor: '#f97316' },
  { id: 'nav-whatsapp', label: 'WA Broadcast', href: '/whats-app-broadcast-management', icon: FaWhatsapp, group: 'acquisition', activeColor: '#22c55e' },
  { id: 'nav-pipeline', label: 'Pipeline FU', href: '/pipeline-editor', icon: Hierarchy3, group: 'acquisition', activeColor: '#ec4899' },
  { id: 'nav-livechat', label: 'Live Chat & AI Bot', href: '/live-chat', icon: Messages1, group: 'acquisition', activeColor: '#0d9488' },
  { id: 'nav-projects', label: 'Project', href: '/projects', icon: Briefcase, group: 'delivery', activeColor: '#8b5cf6' },
  { id: 'nav-tasks', label: 'To Do List', href: '/tasks', icon: Task, group: 'delivery', activeColor: '#f43f5e' },
  { id: 'nav-assets', label: 'Digital Assets', href: '/digital-assets', icon: FolderCloud, group: 'delivery', activeColor: '#f59e0b' },
  { id: 'nav-lp-management', label: 'LP Editor', href: '/lp-management', icon: Monitor, group: 'delivery', activeColor: '#0ea5e9' },
  { id: 'nav-invoices', label: 'Invoice', href: '/invoices', icon: ReceiptItem, group: 'delivery', activeColor: '#10b981' },
  { id: 'nav-users', label: 'Manajemen User', href: '/users', icon: SecurityUser, group: 'system', adminOnly: true, activeColor: '#e11d48' },
  { id: 'nav-reports', label: 'Laporan', href: '/reports', icon: Chart, group: 'system', activeColor: '#64748b' },
  { id: 'nav-gallery', label: 'Gallery', href: '/gallery', icon: Gallery, group: 'system', activeColor: '#a855f7' },
  { id: 'nav-settings', label: 'Pengaturan', href: '/settings', icon: Setting2, group: 'system', activeColor: '#475569' },
];

const groupLabels: Record<string, string> = {
  overview: 'Overview',
  acquisition: 'Akuisisi',
  delivery: 'Delivery',
  system: 'Sistem',
};

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [badges, setBadges] = useState<{ leads: number; broadcasts: number; chats: number; projects: number; invoices: number } | null>(null);
  const pathname = usePathname();
  const { startTour } = useTour({ userTourCompleted: user?.tourCompleted });

  useEffect(() => {
    fetch('/api/auth/me')
      .then(res => res.json())
      .then(data => {
        if (data.user) setUser(data.user);
      })
      .catch(err => console.error('Failed to fetch user', err))
      .finally(() => setIsLoadingUser(false));
      
    getSidebarBadges().then(setBadges).catch(console.error);
  }, []);

  useEffect(() => {
    const handleToggle = () => setIsMobileOpen(prev => !prev);
    window.addEventListener('toggle-mobile-sidebar', handleToggle);
    return () => window.removeEventListener('toggle-mobile-sidebar', handleToggle);
  }, []);

  const groups = ['overview', 'acquisition', 'delivery', 'system'];

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed', error);
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-[190] md:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      <aside
        className={`flex flex-col h-full transition-transform duration-300 ease-in-out shrink-0 bg-[#1c1c1c] fixed md:relative z-[200] top-0 bottom-0 left-0 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
        style={{
          width: collapsed ? '68px' : '260px',
          border: 'none',
        }}
      >
      {/* Brand Header */}
      <div className="p-5 flex items-center justify-center shrink-0 relative">
        <div className="flex items-center gap-1 min-w-0">
          <img src="/logo.png" alt="STUDO.ID" className="h-8 w-8 object-contain shrink-0" />
          {!collapsed && (
            <span className="text-white font-bold text-lg tracking-tight">
              STUDO<span style={{ color: '#8cc63f' }}>.ID</span>
            </span>
          )}
        </div>
        {!collapsed && (
          <button
            onClick={() => setCollapsed(true)}
            className="absolute right-4 p-1.5 rounded-[var(--radius)] transition-colors hover:bg-white/5"
            style={{ color: 'var(--muted-foreground)' }}
          >
            <ChevronLeft size={16} style={{ color: '#555' }} />
          </button>
        )}
      </div>

      {collapsed && (
        <div className="flex justify-center shrink-0 mb-4">
          <button
            onClick={() => setCollapsed(false)}
            className="p-2 rounded-[var(--radius)] transition-colors hover:bg-white/5"
            style={{ color: 'var(--muted-foreground)' }}
            title="Expand sidebar"
          >
            <ArrowRight3 variant="Bulk" size={18} style={{ color: '#555' }} />
          </button>
        </div>
      )}

      {/* Global Search */}
      {!collapsed && (
        <div className="px-4 pb-4 shrink-0">
          <div
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-[4px]"
            style={{ background: '#222', border: '1px solid var(--border)' }}
          >
            <Search size={14} style={{ color: '#ffffff', opacity: 0.5 }} />
            <input
              type="text"
              placeholder="Search"
              className="bg-transparent outline-none text-[12px] flex-1 w-full"
              style={{ color: '#b3b3b3' }}
            />
            <span
              className="text-[9px] px-1 py-0.5 rounded-[3px] opacity-40 bg-white/5"
              style={{ color: '#b3b3b3' }}
            >
              ⌘F
            </span>
          </div>
        </div>
      )}
      {collapsed && (
        <div className="px-4 pb-4 shrink-0 flex justify-center">
          <button className="w-10 h-10 flex items-center justify-center rounded-[var(--radius)] hover:bg-white/5 transition-colors" style={{ color: 'var(--muted-foreground)' }}>
            <SearchNormal1 variant="Bulk" size={18} />
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className={`flex-1 overflow-y-auto overflow-x-hidden ${collapsed ? 'px-2' : 'px-4'}`}>
        {groups.map((group) => {
          let items = navItems.filter((n) => n.group === group);

          // Filter admin-only items
          // Jika loading, biarkan adminOnly tetap muncul (optimistic) agar tidak flickering.
          // Hanya sembunyikan jika loading selesai DAN user terbukti bukan admin.
          if (!isLoadingUser && user && user.role !== 'admin') {
            items = items.filter(n => !n.adminOnly);
          }

          if (items.length === 0) return null;

          return (
            <div key={`group-${group}`} className="mb-6">
              {!collapsed && (
                <p
                  className="text-[11px] font-500 capitalize px-4 mb-2"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  {groupLabels[group]}
                </p>
              )}
              {items.map((item) => {
                const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
                
                let dynamicBadge = item.badge;
                if (badges) {
                  if (item.id === 'nav-leads') dynamicBadge = badges.leads;
                  else if (item.id === 'nav-whatsapp') dynamicBadge = badges.broadcasts;
                  else if (item.id === 'nav-livechat') dynamicBadge = badges.chats;
                  else if (item.id === 'nav-projects') dynamicBadge = badges.projects;
                  else if (item.id === 'nav-invoices') dynamicBadge = badges.invoices;
                }
                const hasBadge = dynamicBadge !== undefined && dynamicBadge > 0;

                return (
                  <Link
                    key={item.id}
                    id={item.id}
                    href={item.href}
                    onClick={() => setIsMobileOpen(false)}
                    title={collapsed ? item.label : undefined}
                    className={`
                      flex items-center rounded-[4px] mb-0.5 text-[13px]
                      transition-all duration-150 relative group
                      ${collapsed ? 'justify-center w-10 h-10 mx-auto px-0' : 'gap-3 px-4 py-1.5'}
                      ${isActive ? 'bg-[#2a2a2a] text-white font-medium' : 'sidebar-item-hover font-medium text-[#999999]'}
                    `}
                  >
                    <span
                      className={`shrink-0 flex items-center justify-center ${isActive ? 'opacity-100' : 'opacity-80'}`}
                      style={{
                        width: '20px',
                        height: '20px',
                        color: isActive ? item.activeColor : '#b3b3b3'
                      }}
                    >
                      {item.id === 'nav-whatsapp' ? (
                        <item.icon size={16} />
                      ) : (
                        <item.icon variant="Bulk" size={20} color={isActive ? item.activeColor : '#b3b3b3'} />
                      )}
                    </span>
                    {!collapsed && (
                      <span className="truncate flex-1">{item.label}</span>
                    )}
                    {!collapsed && hasBadge && (
                      <span
                        className="text-[10px] font-600 px-1.5 py-0.5 rounded-[3px] tabular-nums"
                        style={{
                          background: isActive ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.06)',
                          color: isActive ? '#ffffff' : 'var(--muted-foreground)',
                        }}
                      >
                        {dynamicBadge}
                      </span>
                    )}
                    {collapsed && hasBadge && (
                      <span
                        className="absolute top-2 right-2 w-2 h-2 rounded-full border border-[#1c1c1c]"
                        style={{ background: 'var(--foreground)' }}
                      />
                    )}
                    {/* Tooltip for collapsed */}
                    {collapsed && (
                      <span
                        className="absolute left-full ml-3 px-2 py-1 text-xs rounded-[var(--radius)] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50"
                        style={{
                          background: 'var(--card)',
                          color: 'var(--foreground)',
                          border: '1px solid var(--border)',
                        }}
                      >
                        {item.label}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className={`shrink-0 mt-auto ${collapsed ? 'px-2 pb-4' : 'px-4 pb-4'}`}>
        <div className="border-t border-white/[0.06] mb-4" />
        <button
          onClick={() => startTour()}
          className={`
            flex items-center rounded-[6px] text-[13px] font-medium relative group
            transition-all duration-200 ease-out mb-2
            ${collapsed ? 'justify-center w-10 h-10 mx-auto px-0' : 'w-full gap-3 px-4 py-2.5'}
            text-[#777] hover:text-[#6366f1]
            hover:bg-[rgba(99,102,241,0.08)]
          `}
          title={collapsed ? 'Tour Panduan' : undefined}
        >
          <span className="shrink-0 flex items-center justify-center">
            <HelpCircle size={17} />
          </span>
          {!collapsed && <span className="truncate">Tour Panduan</span>}
          {collapsed && (
            <span
              className="absolute left-full ml-3 px-2.5 py-1.5 text-xs font-medium rounded-[6px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-50 translate-x-1 group-hover:translate-x-0"
              style={{
                background: 'rgba(28,28,28,0.95)',
                color: '#6366f1',
                border: '1px solid rgba(99,102,241,0.15)',
                backdropFilter: 'blur(8px)',
              }}
            >
              Tour Panduan
            </span>
          )}
        </button>
        <button
          onClick={handleLogout}
          className={`
            sidebar-logout-btn
            flex items-center rounded-[6px] text-[13px] font-medium relative group
            transition-all duration-200 ease-out
            ${collapsed ? 'justify-center w-10 h-10 mx-auto px-0' : 'w-full gap-3 px-4 py-2.5'}
            text-[#777] hover:text-red-400
            hover:bg-red-500/[0.08]
          `}
          title={collapsed ? 'Logout' : undefined}
        >
          <span className="shrink-0 flex items-center justify-center transition-transform duration-200 group-hover:translate-x-[2px]">
            <LogOut size={17} />
          </span>
          {!collapsed && <span className="truncate">Keluar</span>}
          {/* Tooltip for collapsed */}
          {collapsed && (
            <span
              className="absolute left-full ml-3 px-2.5 py-1.5 text-xs font-medium rounded-[6px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-50 translate-x-1 group-hover:translate-x-0"
              style={{
                background: 'rgba(28,28,28,0.95)',
                color: '#f87171',
                border: '1px solid rgba(248,113,113,0.15)',
                backdropFilter: 'blur(8px)',
              }}
            >
              Logout
            </span>
          )}
        </button>
      </div>
    </aside>
    </>
  );
}
